/**
 * WEB-989 — фон поверхности воронки на <html>/<body> публичной страницы.
 *
 * Корень бага: поверхность воронки (тема/фон/палитра) красится только на
 * <main> высотой 100dvh, а html/body остаются на кабинетной
 * --color-bg-layout (global.css) — на светлой теме это белый. На телефоне
 * видимая высота живая: адресная строка прячется/появляется при скролле, dvh
 * меняется, и в момент перехода (а также при оверскролле-«резинке» и при
 * открытой клавиатуре) браузер показывает область ЗА пределами main — белую.
 * Отсюда «белые полосы внизу» и заметное на мобилках дёргание.
 *
 * Механизм — служебный <style> в конце <head>, а не инлайн на узлах:
 *   • значение фона уходит в CSS текстом — градиенты и var() доживают до
 *     браузера (инлайновый shorthand с градиентом jsdom вообще отбрасывает —
 *     тот же класс граблей, что «jsdom отбрасывает var() в инлайновом
 *     opacity», см. WEB-925);
 *   • конец <head> = позже global.css → при равной специфичности выигрывает
 *     наше правило, !important не нужен;
 *   • снятие = удаление узла, html/body возвращаются под власть CSS без
 *     сохранения/восстановления инлайна.
 *
 * Красим ОБА узла: цвет «холста» (canvas) браузер берёт с <html>, когда у
 * того есть собственный фон — а он есть; body одного не хватило бы.
 *
 * Механизм min-height 100vh/100dvh (WEB-973) и flex-колонку WEB-965 не
 * трогаем — это только слой подстраховки цветом.
 */
import { useLayoutEffect } from 'react';
import type { CSSProperties } from 'react';

export const FUNNEL_SURFACE_STYLE_ATTR = 'data-web989-funnel-surface';

/**
 * Фон приходит из НАСТРОЕК воронки (пользовательский ввод: цвет темы, свой
 * hex, градиент), а уходит в текст CSS-правила — значения со структурными
 * символами CSS не пропускаем, чтобы кривое/злонамеренное значение не могло
 * вырваться за пределы объявления. Отказ = фон просто не транслируется
 * (прежнее поведение), страница не ломается.
 */
function isSafeCssValue(value: string): boolean {
  return !/[{};]/.test(value);
}

/**
 * Откуда брать фон страницы из собранной поверхности (buildFunnelSurfaceStyle):
 * явный ключ `background` (тема/градиент/оверрайд/тёплая база) в приоритете;
 * воронка «палитра без темы и без явного фона» ключа background не имеет
 * вовсе — цвет страницы у неё живёт только в переменной --color-bg-primary,
 * а html/body — ПРЕДКИ main и инлайновую переменную main не видят, поэтому
 * берём её значение напрямую.
 */
export function resolveSurfacePageBackground(
  style: CSSProperties,
): string | undefined {
  const explicit = style.background;
  if (typeof explicit === 'string' && explicit.length > 0) return explicit;
  const viaVar = (style as Record<string, unknown>)['--color-bg-primary'];
  return typeof viaVar === 'string' && viaVar.length > 0 ? viaVar : undefined;
}

export function useFunnelSurfaceOnBody(
  background: CSSProperties['background'],
): void {
  useLayoutEffect(() => {
    if (
      typeof background !== 'string' ||
      background.length === 0 ||
      !isSafeCssValue(background)
    ) {
      return undefined;
    }
    const styleEl = document.createElement('style');
    styleEl.setAttribute(FUNNEL_SURFACE_STYLE_ATTR, '');
    styleEl.textContent = `html, body { background: ${background}; }`;
    document.head.appendChild(styleEl);
    return () => {
      styleEl.remove();
    };
  }, [background]);
}
